import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

// Threshold from the original L.Control.ZoomBar implementation:
// approximately 556×552 px — hides the zoom-to-area button on mobile
// devices where pinch-zoom is available instead.
const SMALL_SCREEN_THRESHOLD = 307456;

/**
 * ZoomBarControl – an extended Leaflet zoom control modelled after
 * elrobis/L.Control.ZoomBar (https://github.com/elrobis/L.Control.ZoomBar).
 *
 * Buttons:
 *   ⌂  – Home: return to the map's initial centre/zoom
 *   +  – Zoom in
 *   −  – Zoom out
 *   ⊕  – Zoom to Area: drag-draw a rectangle then zoom to it
 *         (hidden on small screens where pinch-zoom is available)
 */
export default function ZoomBarControl({ position = "topleft" }) {
  const map = useMap();
  const controlRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    // ── Helpers ─────────────────────────────────────────────────────────────
    const isSmallScreen = () =>
      window.screen.availWidth * window.screen.availHeight < SMALL_SCREEN_THRESHOLD;

    let zoomToAreaActive = false;
    let startLatLng = null;
    let zoomRect = null;
    const rectStyle = {
      fillOpacity: 0.15,
      color: "#00c8ff",
      weight: 1,
    };

    // Store the home position once at mount time
    const homeCenter = map.getCenter();
    const homeZoom = map.getZoom();

    // ── Build the control container ──────────────────────────────────────────
    const container = L.DomUtil.create(
      "div",
      "leaflet-control-zoom leaflet-bar leaflet-control"
    );

    const makeButton = (label, title, cls, onClick) => {
      const btn = L.DomUtil.create("a", cls, container);
      btn.innerHTML = label;
      btn.href = "#";
      btn.title = title;
      btn.setAttribute("role", "button");
      btn.setAttribute("aria-label", title);
      // Prevent map clicks propagating through the button
      L.DomEvent.disableClickPropagation(btn);
      L.DomEvent.on(btn, "click", L.DomEvent.preventDefault);
      L.DomEvent.on(btn, "click", onClick);
      return btn;
    };

    // Home button (SVG house icon)
    const homeSvg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">' +
      '<path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>';
    makeButton(homeSvg, "Zoom to start position", "leaflet-control-zoom-home", () => {
      map.setView(homeCenter, homeZoom);
    });

    // Zoom-in button
    const zoomInBtn = makeButton(
      "+",
      "Zoom in",
      "leaflet-control-zoom-in",
      (e) => map.zoomIn(e.shiftKey ? 3 : 1)
    );

    // Zoom-out button
    const zoomOutBtn = makeButton(
      "−",
      "Zoom out",
      "leaflet-control-zoom-out",
      (e) => map.zoomOut(e.shiftKey ? 3 : 1)
    );

    // Zoom-to-Area button (desktop only)
    let zoomToAreaBtn = null;
    if (!isSmallScreen()) {
      // SVG crosshair / area icon
      const areaSvg =
        '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
        '<rect x="3" y="3" width="18" height="18" rx="1"/>' +
        '<line x1="3" y1="3" x2="8" y2="8"/>' +
        '<line x1="21" y1="3" x2="16" y2="8"/>' +
        '<line x1="21" y1="21" x2="16" y2="16"/>' +
        '<line x1="3" y1="21" x2="8" y2="16"/>' +
        "</svg>";
      zoomToAreaBtn = makeButton(
        areaSvg,
        "Zoom to area",
        "leaflet-control-zoom-to-area",
        () => activateZoomToArea()
      );
    }

    // ── Zoom-to-Area logic ───────────────────────────────────────────────────
    const activateZoomToArea = () => {
      if (zoomToAreaActive) return;
      zoomToAreaActive = true;
      L.DomUtil.addClass(map.getContainer(), "leaflet-crosshair");
      map.dragging.disable();
      map.on("mousedown", onMouseDown);
    };

    const onMouseDown = (e) => {
      startLatLng = e.latlng;
      zoomRect = L.rectangle([startLatLng, startLatLng], rectStyle).addTo(map);
      map.on("mousemove", onMouseMove);
      map.on("mouseup", onMouseUp);
    };

    const onMouseMove = (e) => {
      if (!zoomRect || !startLatLng) return;
      zoomRect.setBounds([startLatLng, e.latlng]);
    };

    const onMouseUp = () => {
      map.off("mousedown", onMouseDown);
      map.off("mousemove", onMouseMove);
      map.off("mouseup", onMouseUp);
      L.DomUtil.removeClass(map.getContainer(), "leaflet-crosshair");
      map.dragging.enable();
      zoomToAreaActive = false;

      if (zoomRect) {
        const bounds = zoomRect.getBounds();
        map.removeLayer(zoomRect);
        zoomRect = null;
        startLatLng = null;
        if (bounds.isValid()) {
          map.fitBounds(bounds);
        }
      }
    };

    // ── Disabled state for +/− ───────────────────────────────────────────────
    const updateDisabled = () => {
      const zoom = map.getZoom();
      zoomInBtn.classList.toggle("leaflet-disabled", zoom >= map.getMaxZoom());
      zoomOutBtn.classList.toggle("leaflet-disabled", zoom <= map.getMinZoom());
    };
    map.on("zoomend zoomlevelschange", updateDisabled);
    updateDisabled();

    // ── Add to map as a native L.Control ─────────────────────────────────────
    const ctrl = L.control({ position });
    ctrl.onAdd = () => container;
    ctrl.onRemove = () => {
      map.off("zoomend zoomlevelschange", updateDisabled);
      if (zoomToAreaActive) {
        map.off("mousedown", onMouseDown);
        map.off("mousemove", onMouseMove);
        map.off("mouseup", onMouseUp);
        L.DomUtil.removeClass(map.getContainer(), "leaflet-crosshair");
        map.dragging.enable();
      }
    };
    ctrl.addTo(map);
    controlRef.current = ctrl;

    return () => {
      try {
        ctrl.remove();
      } catch (_) {
        // ctrl.remove() can throw if the map was already destroyed; safe to ignore
      }
    };
  // `position` is captured at mount time. Changing it at runtime is not
  // supported — the entire control would need to be rebuilt. The prop is
  // intentionally not in the dep array to avoid unnecessary rebuilds.
  }, [map]); // map is stable - intentionally omit inner refs from dep array

  return null;
}
